import {
  Saturation,
  Hue,
  hsvaToRgbString,
  hsvaToRgba,
  rgbaToHsva,
  ShadeSlider,
} from '@uiw/react-color';
import { LucideLightbulb, LucideLightbulbOff } from 'lucide-react';
import React, { useEffect } from 'react';
import { XStack, YStack } from 'tamagui';

import { useBacklight, BacklightColor } from '../../../hooks/firmware/useBacklight';
import { StyledCard } from '../../Settings_General/components/styles';

interface ColorPickerCardProps {
  isEnabled: boolean;
}

export const ColorPickerCard: React.FC<ColorPickerCardProps> = ({ isEnabled }) => {
  const [hsva, setHsva] = React.useState({ h: 50, s: 50, v: 50, a: 1 });
  const [brightnessHsva, setBrightnessHsva] = React.useState({ h: 0, s: 0, v: 50, a: 1 });
  const debounceTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const brightnessDebounceTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const { setBacklightCustomColor, backlightColor, backlightBrightness, setBacklightBrightness } =
    useBacklight();
  
  // Helper function to convert BacklightColor to RGB values
  const getColorRGB = (color: BacklightColor): [number, number, number] => {
    switch (color) {
      case 'White':
        return [255, 255, 255];
      case 'Red':
        return [255, 0, 0];
      case 'Yellow':
        return [255, 255, 0];
      case 'Green':
        return [0, 255, 0];
      case 'Blue':
        return [0, 0, 255];
      case 'Custom':
      default:
        return [255, 255, 255]; // Default to white for Custom or unknown
    }
  };

  // Fetch backlight color and brightness on component mount
  useEffect(() => {
    backlightColor.execute();
    backlightBrightness.execute();
  }, []); // Only run on mount

  // Update local hsva state when backlight color data is received or alert state changes
  useEffect(() => {
    let rgbValues: [number, number, number];

    // Show alert color when alerts are active
    if (backlightColor.data) {
      // Show user's color when no alerts
      if (backlightColor.data.custom_rgb) {
        rgbValues = backlightColor.data.custom_rgb;
      } else {
        rgbValues = getColorRGB(backlightColor.data.color);
      }
    } else {
      return; // No data available yet
    }

    const [r, g, b] = rgbValues;
    // Convert RGB to HSVA using the proper conversion function
    const rgba = { r, g, b, a: 1 };
    const hsva = rgbaToHsva(rgba);
    setHsva(hsva);
  }, [backlightColor.data]);

  // Update brightness hsva state when backlight brightness data is received
  useEffect(() => {
    if (backlightBrightness.data?.brightness !== undefined) {
      setBrightnessHsva((prev) => ({ ...prev, v: backlightBrightness.data!.brightness }));
    }
  }, [backlightBrightness.data]);

  // Helper function to update backlight color with debouncing
  const updateBacklightColor = React.useCallback(
    (newHsva: typeof hsva) => {
      if (!isEnabled) return;

      // Clear existing timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      // Set new timeout
      debounceTimeoutRef.current = setTimeout(() => {
        const rgba = hsvaToRgba(newHsva);
        const rgb: [number, number, number] = [
          Math.round(rgba.r),
          Math.round(rgba.g),
          Math.round(rgba.b),
        ];
        setBacklightCustomColor(rgb);
      }, 500); // 500ms debounce
    },
    [isEnabled, setBacklightCustomColor],
  );

  // Helper function to update backlight brightness with debouncing
  const updateBacklightBrightness = React.useCallback(
    (newBrightness: number) => {
      if (!isEnabled) return;

      // Clear existing timeout
      if (brightnessDebounceTimeoutRef.current) {
        clearTimeout(brightnessDebounceTimeoutRef.current);
      }

      // Set new timeout
      brightnessDebounceTimeoutRef.current = setTimeout(() => {
        setBacklightBrightness(newBrightness);
      }, 500); // 500ms debounce
    },
    [isEnabled, setBacklightBrightness],
  );

  // Cleanup timeouts on unmount
  useEffect(
    () => () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (brightnessDebounceTimeoutRef.current) {
        clearTimeout(brightnessDebounceTimeoutRef.current);
      }
    },
    [],
  );

  return (
    <StyledCard height="432px" width="500px" position="relative">
      {!isEnabled && (
        <XStack
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 9,
          }}
        />
      )}
      <XStack flex={1} opacity={isEnabled ? 1 : 0.2}>
        <XStack flex={1} gap="$5">
          <YStack flex={1} gap="$5">
            <Saturation
              style={{ width: '100%', height: '332px', borderRadius: '16px' }}
              onChange={(newHsva) => {
                setHsva(newHsva);
                updateBacklightColor(newHsva);
              }}
              hsva={hsva}
              pointer={({ color, top, left }) => (
                <XStack
                  width={30}
                  height={30}
                  borderRadius="100%"
                  borderWidth={4}
                  borderColor="white"
                  backgroundColor={color}
                  position="absolute"
                  top={top}
                  left={left}
                  style={{
                    transform: 'translate(-50%, -50%)',
                  }}
                />
              )}
            />
            <Hue
              style={{ height: '24px', borderRadius: '16px' }}
              onChange={(newHue) => {
                const newHsva = { ...hsva, h: newHue.h };
                setHsva(newHsva);
                updateBacklightColor(newHsva);
              }}
              hue={hsva.h}
              pointer={({ left }) => {
                // Create pure hue color with full saturation and value
                const pureHueColor = { h: hsva.h, s: 100, v: 100, a: 1 };
                const rgb = hsvaToRgbString(pureHueColor);
                return (
                  <XStack
                    width={30}
                    height={30}
                    borderRadius="100%"
                    borderWidth={4}
                    borderColor="white"
                    backgroundColor={rgb}
                    position="absolute"
                    style={{
                      transform: 'translate(-50%, 0)',
                    }}
                    left={left}
                    top={'-3px'}
                  />
                );
              }}
            />
            {/* <Alpha
              hsva={hsva}
              style={{ height: '24px', borderRadius: '16px' }}
              onChange={(newAlpha) => setHsva((prev) => ({ ...prev, a: newAlpha.a }))}
              pointer={({ left }) => {
                // Create pure hue color with full saturation and value
                const pureHueColor = { h: hsva.h, s: hsva.s, v: hsva.v, a: hsva.a };
                const rgba = hsvaToRgba(pureHueColor);
                return (
                  <XStack
                    width={24}
                    height={24}
                    borderRadius="100%"
                    borderWidth={4}
                    borderColor="$white12"
                    backgroundColor={`rgb(${rgba.r}, ${rgba.g}, ${rgba.b}, ${rgba.a})`}
                    position="absolute"
                    style={{
                      transform: 'translate(-50%, 0)',
                    }}
                    left={left}
                  />
                );
              }}
            /> */}
          </YStack>
          <YStack gap="$3.5" alignItems="center">
            <LucideLightbulb size={32} />
            <ShadeSlider
              style={{ width: '24px', borderRadius: '16px', height: '100%' }}
              hsva={brightnessHsva}
              onChange={(newShade) => {
                setBrightnessHsva((prev) => ({ ...prev, v: newShade.v }));
                updateBacklightBrightness(newShade.v);
              }}
              direction="vertical"
              pointer={({ top }) => (
                <XStack
                  width={30}
                  height={30}
                  borderRadius="100%"
                  borderWidth={4}
                  borderColor="white"
                  backgroundColor="transparent"
                  position="absolute"
                  left={'-3px'}
                  top={top}
                  style={{
                    transform: 'translate(0%, -50%)',
                  }}
                />
              )}
            />
            <LucideLightbulbOff size={32} />
          </YStack>
        </XStack>
      </XStack>
    </StyledCard>
  );
};
